/**
 * caustics — rippling refracted water-light low in a submerged / coastal scene.
 * A few large, soft additive sprites near the floor that shimmer (alpha sine) and
 * wobble horizontally on their own async cycle, evoking sun caught on a water
 * surface. A drifting ambient bed (Z_LAYERS.ATMOSPHERE, mooded:true).
 *
 * Same builder shape as the kit: narrow by kind, place from the seed, mint soft
 * sprites behind IRenderer (null-safe), drive them with ONE shared ctx.addTick,
 * return a sprite handle, self-register. REDUCE-MOTION: the shimmer/wobble is the
 * builder's own tick → when reduced we place the patches STATIC and never tick.
 * The pref is read ONCE at build.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const TWO_PI = Math.PI * 2;

const buildCaustics: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'caustics') return null;
  const { region } = ctx;
  const spanY = region.y1 - region.y0;
  const reduceMotion = getMotionPrefs().reduceMotion;

  const sprites: ISprite[] = [];
  // Per-patch shimmer state: sprite, base x/y, async period + phase.
  const patches: { s: ISprite; baseX: number; baseY: number; periodMs: number; phase: number }[] = [];

  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 8800 + i * 27.7;
    const size = lerp(layer.sizeMin, layer.sizeMax, seededRand(seed));
    const x = lerp(region.x0, region.x1, seededRand(seed + 1));
    const y = region.y0 + spanY * lerp(layer.bandYMin, layer.bandYMax, seededRand(seed + 2));
    const alpha = lerp(layer.alphaMin, layer.alphaMax, 0.5);
    // glow_tight = a sharper-edged cell than glow_soft → reads as a defined caustic
    // ripple on the floor rather than diffuse haze.
    const s = createGlowSprite({ x, y, size, color: layer.color, alpha, textureKey: 'glow_tight', blendMode: 'add', parent: ctx.layerContainer });
    if (!s) break; // textures not ready — quieter scene, never a crash
    sprites.push(s);
    if (!reduceMotion) {
      patches.push({ s, baseX: x, baseY: y, periodMs: lerp(layer.periodMinMs, layer.periodMaxMs, seededRand(seed + 3)), phase: seededRand(seed + 4) });
    }
  }

  if (!sprites.length) return null;
  if (reduceMotion || !patches.length) return makeContentHandle({ sprites });

  let elapsedMs = 0;
  const unsub = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    for (const p of patches) {
      const ph = (elapsedMs / p.periodMs + p.phase) % 1;
      const a = lerp(layer.alphaMin, layer.alphaMax, 0.5 - 0.5 * Math.cos(ph * TWO_PI));
      p.s.setPosition(p.baseX + Math.sin(ph * TWO_PI) * layer.driftPx, p.baseY);
      p.s.setAlpha(a);
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'caustics', build: buildCaustics, depth: Z_LAYERS.ATMOSPHERE });
